import {
  Component,
  OnDestroy,
  ChangeDetectorRef,
  Injector,
} from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { LookupGetService } from '../../../service/lookup-service/lookup-get.service';
import { RepeaterService } from '../../../service/repeater-service/repeater.service';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { environment } from '../../../../../environments/environment';
import { Art57Section } from '../../../ui/Art57Section/Art57Section';
import { testingEnum } from '../../../common/enum/testingEnum';
import { BaseBulkType } from '../../../common/abstract/BaseBulkType';

@Component({
  selector: 'app-bulk-lookup-selector-type',
  templateUrl: './bulk-lookup-selector-type.component.html',
  styleUrl: './bulk-lookup-selector-type.component.scss',
})
export class BulkLookupSelectorTypeComponent
  extends BaseBulkType<FormlyFieldConfig>
  implements OnDestroy {
  queryControl = '';
  showCode = false;
  selectedInput = false;
  lastOrderField: string = 'Name';
  operatorDisabled = false;
  datasource: any[] = [];
  error: string | null = null;
  trackByCode = (_: number, item: any) => item?.code;
  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private injector: Injector,
  ) {
    super();
  }

  ngOnInit(): void {
    this.showCode = this.props['showCode']
      ? this.props['showCode'] == true
      : false;
    this.lastOrderField = this.formControl.parent?.get('lastOrder')?.value
      ? this.formControl.parent?.get('lastOrder')?.value
      : this.lastOrderField;
    this.datasource = this.formControl.parent?.get('datasource')?.value;
  }

  onKeyUp() {
    if (this.props["allowCount"]) {
      this.formControl.parent?.get('term')?.setValue(this.queryControl);
    }
  }

  onEnter() {
    this.fetch(this.props['parameters']);
  }

  setOperator(event: any) {
    this.formControl.parent!.get!('operator')?.setValue(event.target.value);
  }

  private fetch(parameters: any = []) {
    this.error = null;
    this.loading = true;
    this.datasource = [];
    this.cdr.markForCheck();

    this.resultClear();

    let criteria: string = '';

      criteria =
        this.formControl.parent?.get('operator')?.value +
        ':' +
        this.queryControl;


    this.injector
      .get(LookupGetService)
      .filter(this.props['lookupname'], criteria, parameters)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (data) => {

          if (!Array.isArray(data) || data.length === 0) {
            this.injector.get(Art57Section).showTooltipMessage({
              severity: 'warn',
              detail: `${this.field.parent!.parent!.props!['label']
                }: No items found for ${this.queryControl
                  ? this.queryControl
                  : 'empty search'
                }`,
              life: environment.successElapsedTime,
            });
          }

          this.datasource = (Array.isArray(data) ? [...data] : []).map(
            (opt) => {
              const originalName = opt.name ?? '-';
              return {
                code: opt.code,
                name: this.showCode ? `${opt.code} - ${originalName}` : `${opt.code}`,
                originalName: originalName,
              };
            },
          );

          if (
            this.datasource &&
            this.datasource.length >= environment.maxNumberRowsReturned
          ) {
            this.injector!.get(Art57Section).showTooltipMessage({
              severity: 'warn',
              id: testingEnum.maxResultsRetrievedToastMsg,
              detail: `Only ${environment.maxNumberRowsReturned} results were retrieved.  Please refine your search criteria.`,
              life: environment.warningElapsedTime,
            });
          }

          this.order(this.showCode ? this.lastOrderField : 'Name');
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refresh() {
    this.onEnter();
  }

  resultClear() {
    this.formControl?.setValue(null);
    this.formControl.parent?.get('datasource')?.setValue([]);
    this.datasource = this.formControl.parent?.get('datasource')?.value;
  }

  override clear() {
    this.formControl.setValue(null);
    this.operatorDisabled=false;
    this.queryControl="";
    this.formControl.parent!.get!('term')!.setValue(null);
    //CHREQ6 clear the resulta and deselect the checkbox when there is only one item.
    this.datasource = [];
    this.formControl.parent!.get!('datasource')!.setValue(null);
    if (this.field.parent!.parent!.fieldGroup!.length <= 1) {
      this.field.parent!.parent!.parent!.get!('filterStatus').formControl!.setValue(false)
    }
  }

  allowedOrderField(): string {
    const allowedOrder = this.lastOrderField === 'Code' ? 'Name' : 'Code';
    return allowedOrder;
  }

  order(orderField: string) {
    if (!this.datasource || this.datasource.length == 0) {
      return;
    }
    switch (orderField) {
      case 'None':
        this.datasource = this.datasource.map((opt) => {
          return {
            code: opt.code,
            name: this.showCode ? `${opt.originalName}` : `${opt.code}`,
            originalName: opt.originalName,
          };
        });
        break;
      case 'Code':
        this.datasource = this.datasource.map((opt) => {
          return {
            code: opt.code,
            name: this.showCode ? `${opt.code} - ${opt.originalName}` : `${opt.code}`,
            originalName: opt.originalName,
          };
        });
        this.datasource = this.datasource.sort((a, b) => {
          const codeA = a.code;
          const codeB = b.code;

          const isNumA = !isNaN(codeA);
          const isNumB = !isNaN(codeB);

          if (isNumA && isNumB) {
            return Number(codeA) - Number(codeB);
          } else {
            return String(codeA).localeCompare(String(codeB));
          }
        });
        break;

      case 'Name':
        // Build a collator that ignores case (and diacritics if quieres)
        const collator = new Intl.Collator(undefined, {
          sensitivity: 'accent',
          numeric: true,
        });

        this.datasource = this.datasource
          .map((opt) => ({
            code: opt.code,
            name: this.showCode ? `${opt.code} - ${opt.originalName}` : `${opt.code}`,
            originalName: opt.originalName,
          }))
          .sort((a, b) =>
            collator.compare(a.originalName ?? '', b.originalName ?? ''),
          );
        break;
    }
    this.lastOrderField = orderField;
    this.formControl.parent?.get('lastOrder')?.setValue(this.lastOrderField);
    this.formControl.parent?.get('datasource')?.setValue(this.datasource);
  }

  add() {
    const repeaterId = this.field.parent!.parent!.props!['repeaterId'];
    const repeater = this.injector.get(RepeaterService).getRepeater(repeaterId);
    if (repeater) {
      repeater.add();
    }
  }

  repeaterfieldDescription(): string {
    const repeaterId = this.field.parent!.parent!.props!['repeaterId'];
    const repeater = this.injector.get(RepeaterService).getRepeater(repeaterId);
    return repeater.props.fieldDescription;
  }

  repeaterLength(): number {
    const repeaterId = this.field.parent!.parent!.props!['repeaterId'];
    const repeater = this.injector.get(RepeaterService).getRepeater(repeaterId);
    return repeater.field.fieldGroup.length;
  }
  remove() {
    const repeaterId = this.field.parent!.parent!.props!['repeaterId'];
    const repeater = this.injector.get(RepeaterService).getRepeater(repeaterId);

    const currentComponentId =
      this.formControl.parent?.get('componentID')?.value;

    const filterDataModel = this.field?.parent?.parent?.model;
    const index = filterDataModel?.findIndex(
      (item: any) => item.componentID === currentComponentId,
    );

    if (repeater && index !== undefined && index > -1) {
      repeater.remove(index);
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();

    const pastedText = event.clipboardData?.getData('text') || '';
    let cleanText = pastedText
      .split(/\r?\n/) // separa por líneas
      .map((s) => s.replace(/"/g, '')) // elimina comillas dobles
      .map((s) => s.trim()) // quita tabs y espacios delante/detrás
      .filter(Boolean) // elimina líneas vacías
      .join('|'); // únelas con |

    if (cleanText.lastIndexOf('|') === cleanText.length - 1) {
      cleanText = cleanText.substring(0, cleanText.length - 1);
    }

    this.queryControl = cleanText;
    this.operatorDisabled = false;

    if (this.props["allowCount"]) {
      if (cleanText.includes('|')) {
        this.formControl.parent!.get('operator')?.setValue('in');
        this.operatorDisabled = true;
      }
      this.formControl.parent!.get!('term')!.setValue(cleanText);
      this.formControl.parent?.get('datasource')?.setValue([]);
      this.datasource = this.formControl.parent?.get('datasource')?.value;
    }

  }

}
